import { Base64 } from 'js-base64';
import { createCodeContainer, updateCodeContainer } from 'js/actions';
import getAiKeysFromType from 'js/components/pipelines/cards/hooks/getAiKeysFromType';
import useAiContainerData from 'js/components/pipelines/cards/hooks/useAiContainerData';
import useReusableTransforms from 'js/components/pipelines/cards/hooks/useReusableTransforms';
import { UPDATE_DATASET_REQUEST } from 'js/constants/actionTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { isNumber } from 'lodash';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import { TransformType } from 'new/components/ReusableTransform/types';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import { useMemo } from 'react';

export default function useAiContainers(data, selectFields, type) {
  const { id } = data;
  const dispatch = useAppDispatch();
  const { setDraftActions, draftActions } = useDraftContext();
  const { updateNexset } = useNexsetsAdapter();

  const { reusableTransforms, transforms } = useReusableTransforms(type);

  const { formData, setFormData, createdCodeContainer, setCreatedCodeContainer } =
    useAiContainerData(data, type);

  const { typePropFieldKey, typeFieldKey, codeFieldKey, nameFieldKey } = getAiKeysFromType(type);

  const fields = useMemo(() => {
    const { [typeFieldKey]: typeField, ...rest } = selectFields;

    // add reusable tx args as dependent fields
    const argFields = [];
    reusableTransforms.forEach((transform) => {
      const args = transform.custom_config?.props || [];
      args?.forEach((field) => {
        const { label, placeholder, type } = field;
        argFields.push({
          ...field,
          display_name: label,
          description: placeholder || '',
          type: 'string',
          inputType: field.inputType || type || 'text',
          dependsOn: {
            name: typeFieldKey,
            value: String(transform.id),
          },
        });
      });
    });

    return [
      {
        ...typeField,
        options: [
          // ...typeField.options,
          ...reusableTransforms.map((transform) => ({
            label: transform.name,
            value: String(transform.id),
            description: `ID: ${transform.id}${transform.description ? ` - Description: ${transform.description}` : ''}`,
          })),
        ],
      },
      ...argFields,
      // ...Object.values(rest)
    ];
  }, [reusableTransforms]);

  const linkCustomRewriter = useDebouncedCallback(
    (newFormData) => {
      const form = newFormData;
      const code = form[codeFieldKey] || '';
      const name = form[nameFieldKey];
      const codeContainerPayload = {
        name: name || 'Custom Query Rewriter',
        description: null,
        resource_type: 'ai_function',
        output_type: TransformType.CUSTOM,
        ai_function_type: type,
        code_type: 'python',
        reusable: true,
        code_encoding: 'base64',
        code: Base64.encode(code),
      };

      // if the linked code container is not reusable, update the code
      if (createdCodeContainer && createdCodeContainer.reusable) {
        dispatch(updateCodeContainer(createdCodeContainer.id, codeContainerPayload));
        setDraftActions({
          ...draftActions,
          [id]: {
            args: [
              [
                id,
                {
                  code_container_id: createdCodeContainer.id,
                },
              ],
            ],
            actions: [updateNexset],
            requestTypes: [UPDATE_DATASET_REQUEST],
          },
        });
      }

      // else if no linked code container exists or the linked code container is
      // reusable, create a new code container with the entered code
      else {
        dispatch(createCodeContainer(codeContainerPayload)).then(({ result }) => {
          setCreatedCodeContainer(result);
          setDraftActions({
            ...draftActions,
            [id]: {
              args: [
                [
                  id,
                  {
                    code_container_id: result,
                    custom_config: { props: { [typePropFieldKey]: 'custom' } },
                  },
                ],
              ],
              actions: [updateNexset],
              requestTypes: [UPDATE_DATASET_REQUEST],
            },
          });
        });
      }
    },
    [formData, createdCodeContainer],
    500,
  );

  const onFieldUpdated = (name: string, value: any) => {
    let newFormData = { ...formData, [name]: value };

    switch (name) {
      case typeFieldKey:
        if (value === 'custom') {
          newFormData = {
            [typeFieldKey]: 'custom',
            [codeFieldKey]: formData[codeFieldKey],
            [typePropFieldKey]: 'custom',
          };
          linkCustomRewriter(newFormData);
        } else if (transforms[value]?.reusable) {
          if (value === formData[typeFieldKey]) {
            break;
          }
          const codeProps = transforms[value]?.custom_config?.props || [];
          const props = codeProps.reduce((data, prop) => {
            data[prop.name] = prop.default || newFormData?.[prop.name] || '';
            return data;
          }, {});
          newFormData = { ...newFormData, ...props };
          setDraftActions({
            ...draftActions,
            [id]: {
              args: [
                [
                  id,
                  {
                    code_container_id: parseInt(value),
                    custom_config: { props },
                  },
                ],
              ],
              actions: [updateNexset],
              requestTypes: [UPDATE_DATASET_REQUEST],
            },
          });
        }
        break;

      case codeFieldKey:
      case nameFieldKey:
        linkCustomRewriter(newFormData);
        break;
      default:
        const maybeId = newFormData[typeFieldKey];
        const codeProps = transforms[maybeId]?.custom_config?.props || [];
        if (codeProps.length > 0 && isNumber(Number(maybeId))) {
          // This means this can be a default rewriter or nexla enriched rewriter
          const props = codeProps.reduce((data, prop) => {
            data[prop.name] = newFormData?.[prop.name] || prop.default;
            return data;
          }, {});
          const customConfigProps = transforms[parseInt(maybeId)]?.custom_config?.props || [];
          const hasInvalidValues =
            customConfigProps?.length &&
            customConfigProps?.some((prop) => {
              const mapValue = props[prop.name];
              if (prop.isHiddenField && mapValue !== prop.default) {
                return true;
              }
            });

          if (hasInvalidValues) {
            delete draftActions[id];
            setDraftActions({
              ...draftActions,
            });
          } else {
            setDraftActions({
              ...draftActions,
              [id]: {
                args: [
                  [
                    id,
                    {
                      code_container_id: parseInt(maybeId),
                      custom_config: { props },
                    },
                  ],
                ],
                actions: [updateNexset],
                requestTypes: [UPDATE_DATASET_REQUEST],
              },
            });
          }
        }
    }
    setFormData(newFormData);
  };

  return {
    formData,
    fields,
    onFieldUpdated,
  };
}
